import React, { useState, useMemo } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import DataTable from '../../components/reusables/DataTable';
import { Badge } from '@appkit4/react-components/badge';
import Modal from '../../components/reusables/Modal';
import { conflictResolveContext } from '../../types/conflictResolveContext';

// ---- Types ----
type TargetPool = {
    pool: string;
    percentage: string;
};

type ConflictingRule = {
    ruleId: string;
    ruleShortName: string;
    targetPools: TargetPool[];
};

type ConflictRow = {
    assetId: string;
    description: string;
    transactionDate: string;
    currentValue: number;
    conflictingRules: [ConflictingRule, ConflictingRule];
};

type SelectionKey = string;

const makeRowKey = (row: ConflictRow): string =>
    `${row.assetId}::${row.conflictingRules[0].ruleId}::${row.conflictingRules[1].ruleId}`;

interface ResolvePerAssetModalProps {
    visible: boolean;
    onClose: () => void;
    context: conflictResolveContext;
    onResolve?: (
        context: conflictResolveContext,
        assetId: string,
        chosenRule: { ruleId: string; ruleShortName: string }
    ) => void;
}

// ---- Dummy data per source ----
const fixedAssetConflicts: ConflictRow[] = [
    {
        assetId: '473619',
        description: '8.2 Mind & Body',
        transactionDate: '2023-01-03',
        currentValue: 56,
        conflictingRules: [
            {
                ruleId: 'R001',
                ruleShortName: 'High Split Rule',
                targetPools: [{ pool: 'MP', percentage: '100%' }],
            },
            {
                ruleId: 'R002',
                ruleShortName: 'Impact Split Rule',
                targetPools: [{ pool: 'FY', percentage: '80%' }],
            },
        ],
    },
    {
        assetId: '473620',
        description: '8.1 High Impact',
        transactionDate: '2023-01-03',
        currentValue: 45,
        conflictingRules: [
            {
                ruleId: 'R001',
                ruleShortName: 'High Split Rule',
                targetPools: [{ pool: 'MP', percentage: '100%' }],
            },
            {
                ruleId: 'R002',
                ruleShortName: 'Impact Split Rule',
                targetPools: [{ pool: 'FY', percentage: '100%' }],
            },
        ],
    },
];

const projectDataConflicts: ConflictRow[] = [
    {
        assetId: 'P-001',
        description: 'Design / C.D.M / Approvals',
        transactionDate: '2023-06-15',
        currentValue: 6599,
        conflictingRules: [
            {
                ruleId: 'R010',
                ruleShortName: 'Electric Split Rule',
                targetPools: [{ pool: 'NQ', percentage: '100%' }],
            },
            {
                ruleId: 'R011',
                ruleShortName: 'Keyword Rule',
                targetPools: [{ pool: 'KW', percentage: '100%' }],
            },
        ],
    },
    {
        assetId: 'P-002',
        description: 'Space Planning - Initial Survey',
        transactionDate: '2023-06-20',
        currentValue: 4200,
        conflictingRules: [
            {
                ruleId: 'R010',
                ruleShortName: 'Electric Split Rule',
                targetPools: [{ pool: 'NQ', percentage: '100%' }],
            },
            {
                ruleId: 'R012',
                ruleShortName: 'Planning Rule',
                targetPools: [{ pool: 'PL', percentage: '80%' }],
            },
        ],
    },
];

const getConflictsData = (context: conflictResolveContext): ConflictRow[] => {
    // In real app, this would be an API call using context keys
    if (context.source === 'fixedAsset') {
        return fixedAssetConflicts;
    }
    return projectDataConflicts;
};

// ---- Component ----
const ResolvePerAssetModal: React.FC<ResolvePerAssetModalProps> = ({
    visible,
    onClose,
    context,
    onResolve,
}) => {
    const [selections, setSelections] = useState<Record<string, string>>({});

    const conflictsData = getConflictsData(context);

    const handleRuleClick = (row: ConflictRow, rule: ConflictingRule) => {
        const rowKey = makeRowKey(row);
        setSelections((prev) => ({ ...prev, [rowKey]: rule.ruleId }));

        if (onResolve) {
            onResolve(context, row.assetId, {
                ruleId: rule.ruleId,
                ruleShortName: rule.ruleShortName,
            });
        }
    };

    const isSelected = (row: ConflictRow, rule: ConflictingRule): boolean => {
        const rowKey = makeRowKey(row);
        return selections[rowKey] === rule.ruleId;
    };

    // Build title based on source
    const modalTitle =
        context.source === 'projectData'
            ? `Resolve Conflicts — Project ${(context as any).projectId ?? ''}`
            : 'Resolve Conflicts';

    const conflictColumns: ColumnDef<ConflictRow, any>[] = useMemo(
        () => [
            { accessorKey: 'assetId', header: 'Asset ID' },
            { accessorKey: 'description', header: 'Description' },
            { accessorKey: 'transactionDate', header: 'Transaction Date' },
            { accessorKey: 'currentValue', header: 'Current Value' },
            {
                id: 'conflictingRules',
                header: 'Conflicting Rules',
                cell: ({ row }) => (
                    <div className="conflicting-rules-cell">
                        {row.original.conflictingRules.map((rule) => (
                            <button
                                key={rule.ruleId}
                                type="button"
                                className={`rule-panel ${isSelected(row.original, rule) ? 'rule-panel--selected' : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRuleClick(row.original, rule);
                                }}
                            >
                                <span className="rule-panel__name">
                                    {rule.ruleShortName}
                                </span>
                                <div className="rule-panel__pools">
                                    {rule.targetPools.map((pool, i) => (
                                        <Badge
                                            key={i}
                                            value={`${pool.pool} (${pool.percentage})`}
                                        />
                                    ))}
                                </div>
                            </button>
                        ))}
                    </div>
                ),
            },
        ],
        [selections]
    );

    return (
        <Modal
            visible={visible}
            title={modalTitle}
            onClose={onClose}
            modalStyle={{ width: '900px', maxWidth: '95vw' }}
        >
            <div className="resolve-conflicts-modal">
                {/* Show extra context info for project data */}
                {context.source === 'projectData' && context.projectId && (
                    <div className="resolve-conflicts-modal__context-info">
                        <span>Project: {context.projectId}</span>
                        {context.version && (
                            <span> &middot; Version: {context.version}</span>
                        )}
                    </div>
                )}

                <div className="resolve-conflicts-modal__table-wrapper">
                    <DataTable data={conflictsData} columns={conflictColumns} />
                </div>
            </div>
        </Modal>
    );
};

export default ResolvePerAssetModal;
